<template>
  <!-- 图片容器，用于显示消息中的图片 -->
  <div
    ref="skeletonDomRef"
    class="image-container"
    @click.self="toggleShow"
  >
    <!-- <img
      :class="['message-image', !isPC && 'message-image-h5']"
      :src="props.content.url"
      :width="props.content.width"
      :height="props.content.height"
    > -->
    <img
      :class="['message-image', !isPC && 'message-image-h5']"
      :src="imageData.url"
      :width="imageData.width"
      :height="imageData.height"
      @click.stop="toggleShow"
    >
    <!-- 大图预览弹窗 -->
    <div v-if="showLargeImage" class="large-image-modal" @click="closeLargeImage">
      <div class="large-image-wrapper" @click.stop>
        <!-- 图片容器，用于处理拖拽和缩放 -->
        <div class="image-container" ref="imageContainerRef">
          <div 
            class="image-drag-container"
            :style="{
              transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`,
              cursor: isDragging ? 'grabbing' : 'grab'
            }"
            @mousedown="startDrag"
          >
            <!-- 大图预览图片 -->
            <img
              :src="imageData.url"
              class="large-image"
              draggable="false"
            />
          </div>
        </div>
        <!-- 关闭按钮 -->
        <div class="close-btn" @click="closeLargeImage">×</div>
        <!-- 缩放控制面板 -->
        <div class="zoom-controls">
          <div class="zoom-btns">
            <button class="zoom-btn" @click="zoomIn" title="放大">
              <i class="icon-zoom-in"></i>
            </button>
            <button class="zoom-btn" @click="zoomOut" title="缩小">
              <i class="icon-zoom-out"></i>
            </button>
            <button class="zoom-btn" @click="resetZoom" title="重置">
              <i class="icon-reset"></i>
            </button>
          </div>
          <!-- 显示当前缩放比例 -->
          <div class="zoom-info">{{ Math.round(scale * 100) }}%</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// 导入必要的Vue组合式API
import { watchEffect, ref, watch, onMounted, withDefaults, onUnmounted } from '../../../../adapter-vue';
// 导入消息模型接口
import { IMessageModel } from '@tencentcloud/chat-uikit-engine';
// 导入环境判断工具
import { isPC } from '../../../../utils/env';
// 导入图片尺寸处理工具
import { handleSkeletonSize } from '../../utils/utils';
// 导入图片消息内容接口
import type { IImageMessageContent } from '../../../../interface';

// 定义组件事件
const emits = defineEmits(['previewImage']);

// 定义组件属性
const props = withDefaults(
  defineProps<{
    content: IImageMessageContent;  // 图片消息内容
    messageItem: IMessageModel;     // 消息项
  }>(),
  {
    content: () => ({}),
    messageItem: () => ({} as IMessageModel),
  },
);

// 响应式状态定义
const skeletonDomRef = ref();        // 图片容器引用
const imageData = ref({});           // 图片数据
const showLargeImage = ref(false);   // 是否显示大图
const scale = ref(1);                // 缩放比例
const translate = ref({ x: 0, y: 0 }); // 图片位移
let dragging = false;                // 拖拽状态
let last = { x: 0, y: 0 };          // 上次鼠标位置
const isDragging = ref(false);       // 是否正在拖拽
const imageContainerRef = ref();     // 大图容器引用

// 组件挂载时
onMounted(() => {
  // 如果消息状态为成功或完成，自动调整图片尺寸
  if (
    props.messageItem?.status === 'success'
    || props.messageItem?.status === 'fail'
    || props.messageItem?.progress === 1
  ) {
    autoFixSkeletonSize();
  }
  // 添加滚轮事件监听，用于缩放
  const container = imageContainerRef.value;
  if (container) {
    container.addEventListener('wheel', handleWheel, { passive: false });
  }
});

// 组件卸载时清理事件监听
onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', endDrag);
  const container = imageContainerRef.value;
  if (container) {
    container.removeEventListener('wheel', handleWheel);
  }
});

// 监听图片数据变化
watchEffect(() => {
  // 处理不同格式的图片数据
  if(props.content.MsgType) {
    const ImageInfoArray = props.content?.MsgContent?.ImageInfoArray[0] || {};
    imageData.value = {
      url: ImageInfoArray.URL || '',
      width: ImageInfoArray.Width || '',
      height: ImageInfoArray.Height || '',
    }
  }else {
    imageData.value = props.content
  }
});

// 监听图片高度变化，自动调整尺寸
watch(
  () => props.content.height,
  (newVal: number, oldVal: number) => {
    if (newVal > oldVal) {
      autoFixSkeletonSize();
    }
  },
);

// 自动调整图片尺寸
function autoFixSkeletonSize() {
  const { width = 0, height = 0 } = props.content;
  if (width === 0 || height === 0) return;
  const containerWidth = document.getElementById('app')?.clientWidth || 0;
  const max = !isPC ? Math.min(containerWidth - 180, 300) : 300;
  const size = handleSkeletonSize(width, height, max, max);
  skeletonDomRef?.value?.style && (skeletonDomRef.value.style.width = `${size.width}px`);
  skeletonDomRef?.value?.style && (skeletonDomRef.value.style.height = `${size.height}px`);
}

// 显示大图预览
function toggleShow() {
  if (props.messageItem?.status === 'success' || props.messageItem?.progress === 1) {
    emits('previewImage', props.messageItem);
  }
  showLargeImage.value = true;
}

// 放大图片
function zoomIn() {
  scale.value = Math.min(scale.value + 0.2, 5);
}

// 缩小图片
function zoomOut() {
  scale.value = Math.max(scale.value - 0.2, 0.2);
}

// 重置缩放和位置
function resetZoom() {
  scale.value = 1;
  translate.value = { x: 0, y: 0 };
}

// 开始拖拽
function startDrag(e: MouseEvent) {
  e.preventDefault();
  isDragging.value = true;
  dragging = true;
  last = { x: e.clientX, y: e.clientY };
  document.body.style.cursor = 'grabbing';
  
  // 添加全局鼠标事件监听
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', endDrag);
}

// 拖拽中
function onDrag(e: MouseEvent) {
  if (!dragging) return;
  e.preventDefault();
  
  const deltaX = e.clientX - last.x;
  const deltaY = e.clientY - last.y;
  
  // 添加边界检查，防止图片被拖出可视区域太远
  const container = imageContainerRef.value;
  if (container) {
    const rect = container.getBoundingClientRect();
    const maxX = rect.width * (scale.value - 1) / 2;
    const maxY = rect.height * (scale.value - 1) / 2;
    
    translate.value.x = Math.max(Math.min(translate.value.x + deltaX, maxX), -maxX);
    translate.value.y = Math.max(Math.min(translate.value.y + deltaY, maxY), -maxY);
  }
  
  last = { x: e.clientX, y: e.clientY };
}

// 结束拖拽
function endDrag() {
  isDragging.value = false;
  dragging = false;
  document.body.style.cursor = 'default';
  
  // 移除全局鼠标事件监听
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', endDrag);
}

// 关闭大图预览
function closeLargeImage() {
  showLargeImage.value = false;
  resetZoom();
}

// 处理鼠标滚轮缩放
function handleWheel(e: WheelEvent) {
  e.preventDefault();
  if (e.deltaY < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
}
</script>

<style lang="scss" scoped>
// 导入公共样式
@import "../../../../assets/styles/common";

// 图片容器样式
.image-container {
  overflow: hidden;
  background-color: #f4f4f4;

  // 消息中的缩略图样式
  .message-image {
    max-width: min(calc(100vw - 180px), 300px);
    max-height: min(calc(100vw - 180px), 300px);
    width: inherit;
    height: inherit;

    // 移动端样式
    &-h5 {
      max-width: calc(100vw - 180px);
      max-height: calc(100vw - 180px);
    }
  }
}

// 大图预览模态框样式
.large-image-modal {
  position: fixed;
  z-index: 9999;
  left: 0; top: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
}

// 大图包装器样式
.large-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

// 图片容器样式
.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

// 拖拽容器样式
.image-drag-container {
  position: relative;
  display: inline-block;
  will-change: transform;
  user-select: none;
  -webkit-user-drag: none;
}

// 大图样式
.large-image {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}

// 缩放控制面板样式
.zoom-controls {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  border-radius: 20px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  backdrop-filter: blur(10px);
}

// 缩放按钮组样式
.zoom-btns {
  display: flex;
  gap: 8px;
}

// 缩放按钮样式
.zoom-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  // 悬停效果
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
  
  // 点击效果
  &:active {
    transform: scale(0.95);
  }
}

// 缩放比例显示样式
.zoom-info {
  color: white;
  font-size: 14px;
  min-width: 60px;
  text-align: center;
}

// 放大图标样式
.icon-zoom-in::before {
  content: "+";
  font-size: 20px;
}

// 缩小图标样式
.icon-zoom-out::before {
  content: "-";
  font-size: 20px;
}

// 重置图标样式
.icon-reset::before {
  content: "↺";
  font-size: 18px;
}

// 关闭按钮样式
.close-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1000;
  
  // 悬停效果
  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
  
  // 点击效果
  &:active {
    transform: scale(0.95);
  }
}
</style>
